import { Cell as CellType, Row } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { CSSProp } from 'styled-components';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useRowIndex } from '@paradigm/design-system/src/components/Table';
import { MobileExpandCell, RowComponent, TdCell } from './common';
import {
  isCellMobileVisibleCollapsed,
  isCellMobileVisibleExpanded,
} from './helpers';
import ExpandedMobileContent from './ExpandedMobileCells';
import Cell from './Cell';

type TableRowProps<T> = {
  row: Row<T>;
  isExpandable: boolean;
  dataTestId: string;
  makeStyle?: (row: Row<T>, index: number) => CSSProp;
};

const TableRow = <T,>({
  row,
  isExpandable,
  dataTestId,
  makeStyle,
  ...rest
}: TableRowProps<T>) => {
  const index = useRowIndex();
  const isMobile = useIsMobile();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const onExpandClick = () =>
    isExpandable && isMobile && setIsExpanded(!isExpanded);

  const visibleCells = row.getVisibleCells();

  const haveSubRows = useMemo(() => row.subRows.length !== 0, [row.subRows]);

  const isCellVisible = useCallback(
    (cell: CellType<T, unknown>) =>
      !isMobile || isCellMobileVisibleCollapsed(cell),
    [isMobile],
  );

  const responsiveVisibleCells = useMemo(
    () => visibleCells.filter(isCellVisible),
    [isCellVisible, visibleCells],
  );

  const responsiveExpandedCells = useMemo(
    () => (isExpanded ? visibleCells.filter(isCellMobileVisibleExpanded) : []),
    [isExpanded, visibleCells],
  );

  return (
    <>
      <RowComponent
        expanded={isMobile && isExpanded}
        key={row.id}
        data-testid={dataTestId}
        onClick={onExpandClick}
        css={makeStyle?.(row, index)}
        {...rest}
      >
        {responsiveVisibleCells.map((cell: CellType<T, unknown>) => {
          const cellWidth = cell.column.columnDef.size;
          return (
            <TdCell cellWidth={cellWidth} key={cell.id}>
              <Cell row={row} cell={cell} />
            </TdCell>
          );
        })}

        {isExpandable && (
          <TdCell
            hidden={!isMobile || haveSubRows}
            css="cursor:pointer"
            cellWidth={11}
            onClick={onExpandClick}
          >
            <MobileExpandCell expanded={isExpanded} />
          </TdCell>
        )}
      </RowComponent>

      {isExpandable && (
        <ExpandedMobileContent
          hidden={!isMobile}
          isExpanded={isExpanded}
          expandedCells={responsiveExpandedCells}
        />
      )}
    </>
  );
};

export default TableRow;
